/**
 * Streak calculation helpers.
 *
 * These functions operate on sets of YYYY-MM-DD date strings (UTC) and
 * provide deterministic streak computations used in several UI pages.
 */

export function calculateCurrentStreak(daySet: Set<string>): number {
  if (!daySet || daySet.size === 0) return 0;

  // Use the most recent date present in the set as the cursor. This makes the
  // streak calculation count the consecutive run ending at the latest
  // session date (so a run that ends on yesterday will still return a
  // positive streak even if there is no session for today).
  const dates = Array.from(daySet);
  // ISO YYYY-MM-DD sorts lexicographically so the last element is the most
  // recent date.
  const lastDateStr = dates.sort()[dates.length - 1];
  const cursor = new Date(`${lastDateStr}T00:00:00Z`);

  let streak = 0;
  let maxIterations = 366; // safety guard
  while (maxIterations > 0 && daySet.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    maxIterations -= 1;
  }

  return streak;
}

export function computeStreaks(daySet: Set<string>) {
  if (!daySet || daySet.size === 0) return { current: 0, longest: 0 };

  const dates = Array.from(daySet).sort();
  let longest = dates.length > 0 ? 1 : 0;
  let currentRun = 1;

  for (let i = 1; i < dates.length; i += 1) {
    const previous = new Date(`${dates[i - 1]}T00:00:00Z`);
    const current = new Date(`${dates[i]}T00:00:00Z`);
    const difference =
      (current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24);
    if (difference === 1) {
      currentRun += 1;
    } else if (difference > 1) {
      longest = Math.max(longest, currentRun);
      currentRun = 1;
    }
  }

  longest = Math.max(longest, currentRun);
  const current = calculateCurrentStreak(daySet);

  return { current, longest };
}
